// 자격증별 공식 과목명 매핑 + 데이터 태그 정규화
// 목적: 흩어진 subject 태그(띄어쓰기/별칭/오타/cert키 오염)를 공식 과목으로 통일해
//       과목별 집중 연습에서 깔끔한 과목 목록을 제공한다.

// 공식 과목(검정 기준). 데이터 태그가 이 중 하나로 정규화됨.
export const OFFICIAL_SUBJECTS = {
  'electrical-engineer': ['전기자기학', '전력공학', '전기기기', '회로이론 및 제어공학', '전기설비기술기준 및 판단기준'],
  'electrical-industrial-engineer': ['전기자기학', '전력공학', '전기기기', '회로이론', '전기설비기술기준 및 판단기준'],
  'electrical-construction-engineer': ['전력공학', '전기기기', '회로이론 및 제어공학', '전기설비기술기준 및 판단기준', '전기응용 및 공사재료'],
  'electrical-technician': ['전기 이론', '전기 기기', '전기 설비'],
  'architectural-engineer': ['건축계획', '건축구조', '건축시공', '건축설비', '건축관계법규'],
  'information-processing-engineer': ['소프트웨어 설계', '소프트웨어 개발', '데이터베이스 구축', '프로그래밍 언어 활용', '정보시스템 구축관리'],
  'industrial-safety-engineer': ['안전관리론', '인간공학 및 시스템안전공학', '기계위험방지기술', '전기위험방지기술', '화학설비위험방지기술', '건설안전기술'],
  'industrial-safety-industrial-engineer': ['산업안전관리론', '인간공학 및 시스템안전공학', '기계위험방지기술', '전기 및 화학설비위험방지기술', '건설안전기술'],
  'industrial-hygiene-engineer': ['산업위생학개론', '작업위생측정 및 평가', '작업환경관리대책', '물리적유해인자관리', '산업독성학'],
  'hazardous-materials-industrial-engineer': ['일반화학', '화재예방과 소화방법', '위험물의 성질과 취급'],
  'hvac-refrigeration-industrial-engineer': ['공기조화', '냉동공학', '배관일반', '전기제어공학'],
  'fire-engineer-mechanic': ['소방원론', '소방유체역학', '소방관계법규', '소방기계시설의 구조 및 원리'],
  'fire-protection-engineer-electrical': ['소방원론', '소방전기회로', '소방관계법규', '소방전기시설의 구조 및 원리'],
  'forest-technician': ['조림 및 육림기술', '산림보호', '임업기계일반'],
  'korean-cooking-technician': ['식품위생 및 법규', '식품학', '조리이론과 원가계산', '공중보건'],
  'computer-applications-level1': ['컴퓨터 일반', '스프레드시트 일반', '데이터베이스 일반'],
  'computer-applications-level2': ['컴퓨터 일반', '스프레드시트 일반'],
};

// 데이터 과목이 너무 불규칙해 과목별 연습을 제공하지 않는 자격증(기능사 일부)
export const SUBJECT_DISABLED = new Set(['excavator-operator', 'forklift-operator-technician']);

// 명시적 별칭(prefix 매칭으로 안 잡히는 케이스)
const ALIASES = {
  'architectural-engineer': { '건축법규': '건축관계법규' },
  'hvac-refrigeration-industrial-engineer': { '배관일번': '배관일반' },
};

const norm = (s) => (s || '').replace(/\s+/g, '');

/**
 * 원시 subject 태그 -> 공식 과목명(또는 null=과목 아님/제외).
 */
export function canonicalSubject(certKey, raw) {
  if (!raw) return null;
  const r = raw.trim();
  // cert 키(영문)·"과목 구분 없음"·cert명류 제외
  if (/^[a-z][a-z0-9-]*$/.test(r)) return null;
  if (r === '과목 구분 없음') return null;
  if (/(기능사|기사|산업기사|기능장|기술사)$/.test(r) && !/[ ]/.test(r)) return null; // 종목명이 과목으로 들어온 경우

  const official = OFFICIAL_SUBJECTS[certKey];
  const alias = ALIASES[certKey] || {};
  if (alias[r]) return alias[r];

  if (official) {
    const rn = norm(r);
    // 정확/부분(접두) 매칭
    for (const off of official) {
      const on = norm(off);
      if (on === rn || on.startsWith(rn) || rn.startsWith(on)) return off;
    }
    return null; // 공식 과목에 없으면 제외(잡태그 방지)
  }
  // 공식표 없는 자격증: 띄어쓰기만 통일해서 그대로 사용
  return r;
}

/**
 * 문제 배열 -> [{subject(공식), count}] (과목별 연습용). 제외 대상은 빠짐.
 */
export function groupBySubject(questions, certKey) {
  if (SUBJECT_DISABLED.has(certKey)) return [];
  const counts = {};
  for (const q of questions) {
    const c = canonicalSubject(certKey, q.subject || q.category);
    if (c) counts[c] = (counts[c] || 0) + 1;
  }
  // 공식 순서 유지(있으면), 없으면 빈도순
  const official = OFFICIAL_SUBJECTS[certKey];
  let entries = Object.entries(counts);
  if (official) {
    entries.sort((a, b) => official.indexOf(a[0]) - official.indexOf(b[0]));
  } else {
    entries.sort((a, b) => b[1] - a[1]);
  }
  return entries.map(([subject, count]) => ({ subject, count }));
}
